import AddIcon from '@mui/icons-material/Add';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Alert,
  Box,
  Button,
  List,
  ListItemButton,
  ListItemText,
  Link as MuiLink,
  Paper,
  Skeleton,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  type EvalCompiledRuntime,
  type EvalFixtureDetailResponse,
  type EvalFixtureSet,
  type FixtureAuthoringConfig,
  type FixtureSetCaseReference,
  useArchiveFixtureSetMutation,
  useCreateFixtureSetMutation,
  useCreateFixtureSetVersionMutation,
  useGetFixtureSetQuery,
  useListFixtureSetsQuery,
} from './api/evalApi';
import EvalFixtureEditor from './components/EvalFixtureEditor';

type FixtureDraft = {
  name: string;
  description: string;
  authoringConfig: FixtureAuthoringConfig;
};

type FixtureStatus = {
  message: string;
  severity: 'success' | 'info' | 'warning' | 'error';
};

function runtimePreviewFromDetail(detail?: EvalFixtureDetailResponse): EvalCompiledRuntime | null {
  if (!detail) {
    return null;
  }
  return {
    identity_context: detail.fixture.identity_context ?? {},
    document_context: detail.fixture.document_context ?? {},
    template_variables: detail.fixture.template_variables ?? {},
    summary: detail.preview_summary,
  };
}

export default function EvalFixturesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFixtureId = searchParams.get('fixture_id') ?? '';
  const returnSuiteID = searchParams.get('return_suite_id') ?? searchParams.get('suite_id') ?? '';
  const returnCaseEditor = searchParams.get('return_case_editor') === '1';
  const [editorMode, setEditorMode] = useState<'create' | 'version'>(
    selectedFixtureId ? 'version' : 'create'
  );
  const [status, setStatus] = useState<FixtureStatus | null>(null);
  const [archiveConflictCaseReferences, setArchiveConflictCaseReferences] = useState<
    FixtureSetCaseReference[]
  >([]);
  const [runtimePreviewOverride, setRuntimePreviewOverride] = useState<EvalCompiledRuntime | null>(
    null
  );

  const { data, isLoading, isFetching, error, refetch } = useListFixtureSetsQuery();
  const { data: fixtureDetail } = useGetFixtureSetQuery(selectedFixtureId, {
    skip: !selectedFixtureId,
  });
  const [createFixtureSet, { isLoading: isCreating }] = useCreateFixtureSetMutation();
  const [createFixtureSetVersion, { isLoading: isCreatingVersion }] =
    useCreateFixtureSetVersionMutation();
  const [archiveFixtureSet, { isLoading: isArchiving }] = useArchiveFixtureSetMutation();

  useEffect(() => {
    setRuntimePreviewOverride(null);
    setArchiveConflictCaseReferences([]);
    if (!selectedFixtureId) {
      setEditorMode('create');
    }
  }, [selectedFixtureId]);

  const fixtures = data?.fixture_sets ?? [];
  const selectedFixture = fixtureDetail?.fixture;
  const selectedFixtureCaseReferences =
    archiveConflictCaseReferences.length > 0
      ? archiveConflictCaseReferences
      : (fixtureDetail?.case_references ?? []);
  const selectedFixtureInUse = selectedFixtureCaseReferences.length > 0;
  const selectedFixturePreview = runtimePreviewFromDetail(fixtureDetail);
  const effectivePreview =
    runtimePreviewOverride ?? (editorMode === 'version' ? selectedFixturePreview : null);
  const latestFixtures = useMemo(() => fixtures, [fixtures]);
  const caseFlowActionLabel = returnCaseEditor ? 'Save and use in case' : 'Save and go to cases';

  const showStatus = (message: string, severity: FixtureStatus['severity'] = 'info') => {
    setStatus({ message, severity });
  };

  const navigateToCaseFlow = (fixtureID: string) => {
    const next = new URLSearchParams(searchParams);
    next.delete('tab');
    next.delete('return_to');
    next.delete('return_suite_id');
    next.delete('return_case_editor');
    next.set('fixture_id', fixtureID);
    if (returnSuiteID) {
      next.set('suite_id', returnSuiteID);
    }
    if (returnCaseEditor && returnSuiteID) {
      next.set('case_editor', '1');
    }
    navigate({ pathname: '/admin/evals', search: next.toString() });
  };

  const buildCaseEditPath = (reference: FixtureSetCaseReference) => {
    const params = new URLSearchParams();
    params.set('suite_id', reference.suite_id);
    params.set('edit_case_id', reference.case_id);
    return `/admin/evals?${params.toString()}`;
  };

  const handleSelectFixture = (fixture: EvalFixtureSet) => {
    const next = new URLSearchParams(searchParams);
    next.set('fixture_id', fixture.id);
    setSearchParams(next, { replace: true });
    setEditorMode('version');
    setArchiveConflictCaseReferences([]);
    setStatus(null);
  };

  const handleCreateNew = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('fixture_id');
    setSearchParams(next, { replace: true });
    setEditorMode('create');
    setRuntimePreviewOverride(null);
    setArchiveConflictCaseReferences([]);
    setStatus(null);
  };

  const handleSubmit = async (draft: FixtureDraft, intent: 'stay' | 'use_in_case') => {
    if (editorMode === 'create') {
      const response = await createFixtureSet({
        name: draft.name,
        description: draft.description,
        authoring_config: draft.authoringConfig,
      }).unwrap();
      const next = new URLSearchParams(searchParams);
      next.set('fixture_id', response.fixture.id);
      setSearchParams(next, { replace: true });
      setEditorMode('version');
      setArchiveConflictCaseReferences([]);
      showStatus(
        `Created fixture set ${response.fixture.name} v${response.fixture.version}.`,
        'success'
      );
      setRuntimePreviewOverride({
        identity_context: response.fixture.identity_context ?? {},
        document_context: response.fixture.document_context ?? {},
        template_variables: response.fixture.template_variables ?? {},
        summary: response.preview_summary,
      });
      if (intent === 'use_in_case') {
        navigateToCaseFlow(response.fixture.id);
      }
      return;
    }

    if (!selectedFixtureId) {
      showStatus('Select a fixture before creating a new version.', 'warning');
      return;
    }
    const response = await createFixtureSetVersion({
      fixture_id: selectedFixtureId,
      description: draft.description,
      authoring_config: draft.authoringConfig,
    }).unwrap();
    const next = new URLSearchParams(searchParams);
    next.set('fixture_id', response.fixture.id);
    setSearchParams(next, { replace: true });
    setArchiveConflictCaseReferences([]);
    showStatus(`Created ${response.fixture.name} v${response.fixture.version}.`, 'success');
    setRuntimePreviewOverride({
      identity_context: response.fixture.identity_context ?? {},
      document_context: response.fixture.document_context ?? {},
      template_variables: response.fixture.template_variables ?? {},
      summary: response.preview_summary,
    });
    if (intent === 'use_in_case') {
      navigateToCaseFlow(response.fixture.id);
    }
  };

  const handleArchive = async () => {
    if (!selectedFixture) {
      showStatus('Select a fixture version before archiving.', 'warning');
      return;
    }
    if (selectedFixtureInUse) {
      showStatus(
        `Cannot archive ${selectedFixture.name} v${selectedFixture.version} while eval cases still reference it.`,
        'warning'
      );
      return;
    }

    const confirmed = window.confirm(
      `Archive fixture "${selectedFixture.name}" v${selectedFixture.version}? Archived fixtures are hidden from the manager and can no longer be attached to eval cases.`
    );
    if (!confirmed) {
      return;
    }

    try {
      await archiveFixtureSet({ fixture_id: selectedFixture.id }).unwrap();
      const next = new URLSearchParams(searchParams);
      next.delete('fixture_id');
      setSearchParams(next, { replace: true });
      setEditorMode('create');
      setRuntimePreviewOverride(null);
      setArchiveConflictCaseReferences([]);
      showStatus(
        `Archived fixture ${selectedFixture.name} v${selectedFixture.version}.`,
        'success'
      );
    } catch (archiveError: any) {
      const conflictReferences = archiveError?.data?.case_references;
      if (Array.isArray(conflictReferences)) {
        setArchiveConflictCaseReferences(conflictReferences);
      }
      showStatus(archiveError?.data?.error ?? 'Failed to archive fixture.', 'error');
    }
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto' }}>
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
        <Paper
          variant="outlined"
          sx={{ width: { xs: '100%', lg: 360 }, p: 2, alignSelf: 'flex-start' }}
        >
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreateNew} fullWidth>
              New Fixture
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? '…' : 'Refresh'}
            </Button>
          </Stack>

          <Typography variant="h6" sx={{ mb: 1 }}>
            Fixture Sets
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Versioned transcript-bundle fixtures for eval cases. Select one to create the next
            version from its current authoring config.
          </Typography>

          {isLoading ? (
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={56} />
              <Skeleton variant="rounded" height={56} />
              <Skeleton variant="rounded" height={56} />
            </Stack>
          ) : error ? (
            <Alert severity="error">Failed to load fixture sets.</Alert>
          ) : latestFixtures.length === 0 ? (
            <Alert severity="info">
              No fixture sets yet. Create the first one from the editor.
            </Alert>
          ) : (
            <List disablePadding>
              {latestFixtures.map((fixture) => (
                <ListItemButton
                  key={fixture.id}
                  selected={fixture.id === selectedFixtureId}
                  onClick={() => handleSelectFixture(fixture)}
                >
                  <ListItemText
                    primary={`${fixture.name} (v${fixture.version})`}
                    secondary={fixture.description || 'No description'}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          {status && (
            <Alert severity={status.severity} sx={{ mb: 2 }} onClose={() => setStatus(null)}>
              {status.message}
            </Alert>
          )}
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <NoteAddIcon color="action" />
                <Box>
                  <Typography variant="h6">
                    {editorMode === 'create'
                      ? 'Create a new fixture set'
                      : 'Create a new fixture version'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Use drag-and-drop, file upload, or pasted transcript text. The guided form stays
                    author-focused, and save is the point where backend compile-time validation
                    runs.
                  </Typography>
                </Box>
              </Stack>
              <Box>
                {editorMode === 'version' && selectedFixture && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<ArchiveOutlinedIcon />}
                    onClick={handleArchive}
                    disabled={isArchiving || selectedFixtureInUse}
                  >
                    {isArchiving ? 'Archiving…' : 'Archive Fixture'}
                  </Button>
                )}
              </Box>
            </Stack>
          </Paper>

          {editorMode === 'version' && selectedFixture && selectedFixtureInUse && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {selectedFixture.name} v{selectedFixture.version} is still attached to{' '}
                {selectedFixtureCaseReferences.length} eval
                {selectedFixtureCaseReferences.length === 1 ? ' case' : ' cases'}.
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Move those cases to another fixture before archiving this version.
              </Typography>
              <Stack spacing={0.75}>
                {selectedFixtureCaseReferences.map((reference) => (
                  <MuiLink
                    key={reference.case_id}
                    component={RouterLink}
                    to={buildCaseEditPath(reference)}
                    underline="hover"
                  >
                    {reference.suite_name} / {reference.case_name}
                    {reference.enabled ? '' : ' (disabled)'}
                  </MuiLink>
                ))}
              </Stack>
            </Alert>
          )}

          <EvalFixtureEditor
            mode={editorMode}
            initialFixture={editorMode === 'version' ? selectedFixture : undefined}
            existingRuntimePreview={effectivePreview}
            isSubmitting={isCreating || isCreatingVersion}
            caseFlowActionLabel={caseFlowActionLabel}
            onSubmit={handleSubmit}
          />
        </Box>
      </Stack>
      <Snackbar
        open={status != null}
        autoHideDuration={5000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={(_, reason) => {
          if (reason === 'clickaway') {
            return;
          }
          setStatus(null);
        }}
      >
        <Alert
          severity={status?.severity ?? 'info'}
          onClose={() => setStatus(null)}
          variant="filled"
          sx={{ width: '100%', maxWidth: 640, boxShadow: 6 }}
        >
          {status?.message ?? ''}
        </Alert>
      </Snackbar>
    </Box>
  );
}
